import { uniqBy } from 'lodash';
import { findDuplicates } from '../helpers/array.helpers';
import {
  getElementPropertySource,
  getElementSourceId,
  getElementPropertySourceId,
  getCount,
  isElementExpressionProperty,
  isElementSelectProperty,
  isElementSwitchProperty,
  IPropertiesOrPropertyParent,
  getElementProperties,
  getElementPropertiesByRecipeSource,
  getElementPropertiesByCategorySource,
  isSelectPropertyOptionValue,
  getSelectPropertyOptions,
} from '../helpers/element-property.helpers';
import {
  isElementQuantityExpressionProperty,
  isElementQuantityProperty,
} from '../helpers/element-quantity.helpers';
import { isValidVariableName } from '../helpers/mathjs';
import { isAutoRecipeId } from '../helpers/recipe.helpers';
import { isCountOfType } from '../helpers/count.helpers';
import { ElementCategoryID } from '../models/element-categories.model';
import {
  IElementProperty,
  ElementPropertySource,
  IElementExpressionProperty,
  IElementSelectProperty,
  IElementSwitchProperty,
  ElementPropertyType,
} from '../models/element-property.model';
import { OneOfElements } from '../models/project.model';
import { logOutsideTest } from '@shared/helpers/debug.helpers';
import { throwValidationErrors } from './validation.helpers';
import { isElement } from '../helpers/recursive-element.helpers';
import { ProjectValidationErrors } from './project-validation';
import { getElementName } from '../helpers/element.helpers';
import { getElementCategoryById } from '../templates/categories';
import { isSelectableQuantityUnit } from '../helpers/unit.helpers';
import { selectableQuantityUnits } from '../models/unit.model';
import {
  ElementValidationError,
  GenericValidationError,
  PropertyValidationError,
  ValidationResult,
} from './validation.models';
import { isDataTableProperty } from '@shared/helpers/data-tables';
import { guards } from '@shared/helpers/typeguards.helpers';

const isValidPropertySource = (
  element: OneOfElements,
  property: IElementProperty,
): ValidationResult => {
  const source = getElementPropertySource(property);

  // Undefined source is valid
  if (!source) {
    return true;
  }

  const sourceId = getElementSourceId(element, source);
  const propertySourceId = getElementPropertySourceId(property);

  if (source === ElementPropertySource.Category) {
    const category = getElementCategoryById(
      sourceId as ElementCategoryID,
      false,
    );
    if (!category) {
      return new PropertyValidationError(`Category "${sourceId}" not found`, {
        property,
        element,
      });
    }
  }

  // category_id and recipe_id must match on element and property
  if (sourceId !== propertySourceId) {
    logOutsideTest.info(
      `Invalid ${source} id ${propertySourceId + ''} on ${getElementName(
        element,
      )} - ${property.name}`,
    );
    return new PropertyValidationError(
      `Property ${source} "${propertySourceId + ''}" does not match element ${source} "${sourceId}"`,
      { property, element },
    );
  }
  return true;
};

export const validatePropertySource = <T extends IElementProperty>(
  element: OneOfElements,
  property: T,
): T => {
  throwValidationErrors(isValidPropertySource(element, property));
  return property;
};

const isValidExpressionProperty = (
  property: IElementExpressionProperty,
): ValidationResult => {
  const { min, max } = property;
  const count = getCount(property, false);

  // Must be an Expression
  if (!isCountOfType(count, ElementPropertyType.Expression)) {
    return new PropertyValidationError(
      'Property of type expression has no count or fallbackCount',
      { property },
    );
  }

  // Min value
  if (typeof min === 'number' && count.resolved < min) {
    return new PropertyValidationError('Property value below min', {
      property,
    });
  }

  // Max value
  if (typeof max === 'number' && count.resolved > max) {
    return new PropertyValidationError('Property value above max', {
      property,
    });
  }

  return true;
};

/**
 *
 * @param count
 * @param allowUndefined
 * @returns
 */
export const isValidSelectPropertyCount = (
  count: unknown,
  allowUndefined?: boolean,
  isTableProperty?: boolean,
): count is IElementSelectProperty['count'] => {
  if ((allowUndefined || isTableProperty) && count === undefined) {
    return true;
  }
  return isCountOfType(count, ElementPropertyType.Select);
};

export const isValidSelectProperty = (
  property: IElementSelectProperty,
): ValidationResult => {
  const { inheritFallback, fallbackCount, count, options } = property;

  // Options must be an array or a string key
  if (!guards.is.array(options) && !guards.is.string(options)) {
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.INVALID_OPTIONS,
      { property },
    );
  }

  // Must have at least one option
  if (!getSelectPropertyOptions(property).length) {
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.NO_OPTIONS,
      { property },
    );
  }

  if (!inheritFallback && count === undefined && fallbackCount === undefined) {
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.NO_COUNT_OR_FALLBACK_COUNT,
      { property },
    );
  }
  // Count is allowed to be undefined if inheritFallback is true
  if (
    !isValidSelectPropertyCount(
      count,
      inheritFallback || isDataTableProperty(property),
    )
  ) {
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.INVALID_COUNT,
      { property },
    );
  }
  // Fallback count is allowed to be undefined if count is set
  if (!isValidSelectPropertyCount(fallbackCount, true)) {
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.INVALID_COUNT,
      { property },
    );
  }
  if (count && !isSelectPropertyOptionValue(property, count)) {
    logOutsideTest.warn(
      `Count: "${String(count)}" of property "${property.name}" is not part of options ${getSelectPropertyOptions(
        property,
      )
        .map((o) => `"${o.value}"`)
        .join(', ')}`,
    );
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.COUNT_NOT_IN_OPTIONS,
      { property },
    );
  }
  if (fallbackCount && !isSelectPropertyOptionValue(property, fallbackCount)) {
    logOutsideTest.warn(
      `Fallback count: "${String(fallbackCount)}" of property "${property.name}" is not part of options ${getSelectPropertyOptions(
        property,
      )
        .map((o) => `"${o.value}"`)
        .join(', ')}`,
    );
    return new PropertyValidationError(
      ProjectValidationErrors.PROPERTY.SELECT.COUNT_NOT_IN_OPTIONS,
      { property },
    );
  }
  return true;
};

export const isValidSwitchPropertyCount = (
  count: unknown,
  inheritFallback?: boolean,
): count is IElementSwitchProperty['count'] => {
  if (inheritFallback && count === undefined) {
    return true;
  }
  return isCountOfType(count, ElementPropertyType.Switch);
};

const isValidSwitchProperty = (
  property: IElementSwitchProperty,
): ValidationResult => {
  const { count, inheritFallback } = property;
  if (!isValidSwitchPropertyCount(count, inheritFallback)) {
    return new PropertyValidationError('Invalid count for type switch', {
      property,
    });
  }

  return true;
};

export const isValidProperty = (
  property: IElementProperty | undefined,
): ValidationResult => {
  if (!property) {
    return new GenericValidationError('Property is undefined');
  }
  if (typeof property.id !== 'string' || !property.id) {
    return new PropertyValidationError('Property has no id');
  }
  if (property.recipe_id && property.category_id) {
    return new PropertyValidationError(
      ProjectValidationErrors.INVALID_PROPERTY_SOURCE,
      { property },
    );
  }
  if (isAutoRecipeId(property.recipe_id)) {
    return new PropertyValidationError('Property cannot have auto recipe id', {
      property,
    });
  }
  if (!isValidVariableName(property.name)) {
    return new PropertyValidationError('Invalid property name', { property });
  }

  // Expression
  if (isElementExpressionProperty(property)) {
    return isValidExpressionProperty(property);
  }
  // Select
  else if (isElementSelectProperty(property)) {
    return isValidSelectProperty(property);
  }
  // Switch
  else if (isElementSwitchProperty(property)) {
    return isValidSwitchProperty(property);
  }
  // Invalid property type
  else {
    return new PropertyValidationError('Invalid property type', { property });
  }
};

export const validateElementProperty = <T extends IElementProperty>(
  property: T,
): T => {
  throwValidationErrors(isValidProperty(property));
  return property;
};

export const validateElementProperties = (
  properties?: IElementProperty[],
): IElementProperty[] => {
  throwValidationErrors(isValidElementProperties(properties));
  return properties || [];
};

export const isValidElementProperties = (
  elementOrProps?: IPropertiesOrPropertyParent,
): ValidationResult => {
  if (!elementOrProps) {
    return true;
  }

  const properties = getElementProperties(elementOrProps);

  // All properties must be valid
  for (const property of properties) {
    // Test that property is valid
    const propertyValid = isValidProperty(property);
    if (propertyValid !== true) {
      return propertyValid;
    }

    // Test that sources match if element
    const sourceValid =
      !isElement(elementOrProps) ||
      isValidPropertySource(elementOrProps, property);

    if (sourceValid !== true) {
      return sourceValid;
    }
  }

  const duplicateIds = findDuplicates(properties.map(({ id }) => id));

  // Can't have duplicate ids
  if (duplicateIds.length) {
    return new GenericValidationError('Duplicate property ids');
  }

  const duplicateNames = findDuplicates(properties.map(({ name }) => name));

  // Can't have duplicate names
  if (duplicateNames.length) {
    return new GenericValidationError(
      `Duplicate property names "${duplicateNames.join(', ')}"`,
    );
  }

  const recipeProperties = getElementPropertiesByRecipeSource(elementOrProps);
  const categoryProperties =
    getElementPropertiesByCategorySource(elementOrProps);

  // Names must be unique within recipe
  if (uniqBy(recipeProperties, 'name').length !== recipeProperties.length) {
    return new GenericValidationError(
      'Duplicate property name in recipe properties',
    );
  }
  // Names must be unique within category
  if (uniqBy(categoryProperties, 'name').length !== categoryProperties.length) {
    return new GenericValidationError(
      'Duplicate property name in category properties',
    );
  }

  return true;
};

export const isValidElementQuantityProperties = (
  element: OneOfElements,
): ValidationResult => {
  // Only elements have quantities
  if (!isElement(element)) {
    return true;
  }

  const { quantity, selectedQuantity } = element;

  // Selected quantity must be part of quantity
  if (selectedQuantity && !quantity?.[selectedQuantity]) {
    return new ElementValidationError(
      `Selected quantity "${selectedQuantity}" is not part of quantity`,
      { element },
    );
  }

  // Quantity is not required
  if (!quantity) {
    return true;
  }
  // Sometimes we use an array of quantities. But make sure we never save it like that
  if (Array.isArray(quantity)) {
    return new ElementValidationError('Quantity cannot be an array', {
      element,
    });
  }

  const properties = Object.values(quantity);
  for (const property of properties) {
    if (!isElementQuantityProperty(property)) {
      return new ElementValidationError('Invalid quantity property', {
        element,
      });
    }
    if (
      isElementQuantityExpressionProperty(property) &&
      !isSelectableQuantityUnit(property.unit)
    ) {
      return new ElementValidationError(
        `Invalid quantity property unit "${String(property.unit)}, expected one of ${selectableQuantityUnits.join(', ')}"`,
        { element },
      );
    }
  }
  return true;
};
